import { Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { AuthSessionFacade } from "@services/auth-session/auth-session.facade";
import { CrossAppNavigationService } from "@services/cross-app-navigation/cross-app-navigation.service";
import {
  SascEmailConfigModel,
  SascMenuModel,
  SecretQuestionResponse,
  UserQueryParams,
  UserResponse,
} from "@shared/interfaces/user.model";
import { EMPTY, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { USERROLE } from "src/app/shared/enums/index.enum";

import {
  GET_MENU,
  GET_SASC_EMAIL_CONFIG,
  GET_SECRET_QUESTION,
  GET_USER,
  LOGIN,
  LOGOUT,
  NAVIGATE_LOGIN_PAGE,
  POPULATE_POLICY_MANAGEMENT,
  RESET_LOGIN_RESPONSE,
  RESET_QUERY_PARAMS,
  RESTORE_AUTH_SESSION,
  SAVE_SASC_EMAIL_CONFIG,
  SAVE_USER_VALIDATION,
  SET_IS_PASSWORD_EXPIRED_IND,
  SET_IS_PASSWORD_EXPIRING_IND,
  SET_LOGGED_IN_STATE,
  STORE_QUERY_PARAMS,
  SUBMIT_FIRST_TIME_LOGIN,
  SUBMIT_VALIDATION_QUESTION,
  UPDATE_PREFER_NAME,
} from "./user.action";
import { UserService } from "./user.service";
import {
  normalizeAuthenticatedUser,
  normalizeLoginResponse,
} from "./user-auth.adapter";
import { resolveDashboardPermissionFlags } from "./user-dashboard-permission.utils";

interface UserModel {
  user: Partial<UserResponse>;
  loading: boolean;
  role: string;
  emailConfig: SascEmailConfigModel;
  canViewApprovalRadioButton: boolean;
  canViewApprovalTab: boolean;
  canViewMTApprovalTab: boolean;
  queryParams: UserQueryParams | null;
  menu: SascMenuModel[] | null;
  // Authentication fields from SatUserState
  isLoggedIn: boolean;
  firstTimeLoginState: any;
  secretQuestion: string;
  secretQuestionNo: string;
  isUserSessionActive: boolean;
  lastLoginDate: string;
  error: string;
  errorDesc: string;
  invalidUser: boolean;
  tempUserId: string;
  tempPass: string;
  updatePassword: any;
  disableLoading: boolean;
  secretQuestionError: string;
  isUpdatePassword: boolean;
  secretAnswerWrongPassword: boolean;
  isAdministratorActivated: boolean;
  searchUserResetPasswordErrorTitle: string;
  searchUserResetErrorMsg: string;
  clubMember: any;
  isPasswordExpiring: boolean;
  isPasswordExpired: boolean;
  passwordExpiringInDays: number;
}
@Injectable()
@State<UserModel>({
  name: "UserState",
  defaults: {
    user: null,
    loading: false,
    role: USERROLE.ADMIN,
    emailConfig: null,
    canViewApprovalRadioButton: false,
    canViewApprovalTab: false,
    canViewMTApprovalTab: false,
    queryParams: {},
    menu: null,
    // Authentication defaults
    isLoggedIn: false,
    firstTimeLoginState: null,
    secretQuestion: null,
    secretQuestionNo: null,
    isUserSessionActive: false,
    lastLoginDate: null,
    error: null,
    errorDesc: null,
    invalidUser: false,
    tempUserId: null,
    tempPass: null,
    updatePassword: null,
    disableLoading: false,
    secretQuestionError: null,
    isUpdatePassword: false,
    secretAnswerWrongPassword: false,
    isAdministratorActivated: false,
    searchUserResetPasswordErrorTitle: null,
    searchUserResetErrorMsg: null,
    clubMember: null,
    isPasswordExpiring: false,
    isPasswordExpired: false,
    passwordExpiringInDays: null,
  },
})
export class UserState {
  constructor(
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly authSessionFacade: AuthSessionFacade,
    private readonly crossAppNavigationService: CrossAppNavigationService,
  ) {}

  @Selector()
  public static menu(state) {
    return state.menu;
  }

  @Selector()
  public static menuList(state: UserModel) {
    return state.user.menuList ? state.user.menuList : null;
  }

  @Selector()
  public static user(state: UserModel) {
    return state.user
      ? state.user
      : {
          name: "XXX",
          lastLoginDate: "XX/XX/XXXX XX:XX:XX",
        };
  }

  // Authentication selectors
  @Selector()
  public static isLoggedIn(state: UserModel) {
    return state.isLoggedIn;
  }

  @Selector()
  public static isUserSessionActive(state: UserModel) {
    return state.isUserSessionActive;
  }

  @Selector()
  public static loginError(state: UserModel) {
    return state.error;
  }

  @Selector()
  public static getErrorDesc(state: UserModel) {
    return state.errorDesc;
  }

  @Selector()
  public static getAppUser(state: UserModel) {
    return state.user;
  }

  @Selector()
  public static isUpdatePassword(state: UserModel) {
    return state.isUpdatePassword;
  }

  @Selector()
  public static isPasswordExpired(state: UserModel) {
    return state.isPasswordExpired;
  }

  @Selector()
  public static lastloginDate(state: UserModel) {
    return state.lastLoginDate;
  }

  @Selector()
  public static getFirstTimeLoginState(state: UserModel) {
    return state.firstTimeLoginState;
  }

  @Selector()
  public static secretQuestionDescp(state: UserModel) {
    return state.secretQuestion;
  }

  @Selector()
  public static getSecretQuestionError(state: UserModel) {
    return state.secretQuestionError;
  }

  @Selector()
  public static searchUserResetErrorMsg(state: UserModel) {
    return state.searchUserResetErrorMsg;
  }

  @Selector()
  public static searchUserResetPasswordErrorTitle(state: UserModel) {
    return state.searchUserResetPasswordErrorTitle;
  }

  @Action(GET_USER)
  public getUser({ patchState }: StateContext<UserModel>, action: GET_USER) {
    const { payload } = action;
    patchState({ loading: true });
    return this.userService.getUser(payload.userId).pipe(
      map((res) => {
        const normalizedUser = normalizeAuthenticatedUser(res);
        if (normalizedUser.error) {
          this.clearClientSession(patchState);
          return;
        }

        const dashboardPermissionFlags =
          resolveDashboardPermissionFlags(normalizedUser);
        patchState({
          isLoggedIn: true,
          user: normalizedUser,
          loading: false,
          role: this.getUserRole(normalizedUser),
          ...dashboardPermissionFlags,
          queryParams: payload.queryParams,
          error: null,
          errorDesc: null,
        });
        this.authSessionFacade.setSession(
          normalizedUser,
          payload.queryParams ?? {},
        );
      }),
      catchError(() => {
        this.clearClientSession(patchState);
        return of(null);
      }),
    );
  }

  @Action(RESET_QUERY_PARAMS)
  public clearQueryParams({ patchState }): StateContext<UserModel> {
    return patchState({
      queryParams: null,
    });
  }

  @Action(GET_MENU)
  public getMenuList(
    { getState, patchState }: StateContext<UserModel>,
    { payload }: GET_MENU,
  ) {
    return this.userService.getMenuAccess(payload).pipe(
      map((res) => {
        const menuList = res?.menuList ?? [];
        const state = getState();
        const updatedUser = state.user
          ? {
              ...state.user,
              menuList,
            }
          : state.user;
        patchState({
          user: updatedUser,
          menu: menuList,
        });
        if (updatedUser?.userId) {
          this.authSessionFacade.setSession(
            updatedUser,
            state.queryParams ?? {},
          );
        }
      }),
      catchError(() => {
        return EMPTY;
      }),
    );
  }

  @Action(POPULATE_POLICY_MANAGEMENT)
  public populatePolicyManagement({
    patchState,
    getState,
  }): StateContext<UserModel> {
    const state = getState();
    const copyList = JSON.parse(JSON.stringify(state.user.menuList));
    let menuPolicyManagement;
    if (copyList && copyList.length > 0) {
      copyList.forEach((element) => {
        if (
          element.menuId == "SA_MNPOLICIES" ||
          element.menuId == "MNPOLICIES"
        ) {
          //element.subMenu.forEach(sm => {
          //	let menuSub = sm.link.replace("alpha-sat-svc", "sat-motor-svc");
          //	sm.link = menuSub;
          //});
          menuPolicyManagement = element;
        }
      });
    }
    return patchState({
      menu: menuPolicyManagement,
    });
  }

  @Action(LOGOUT)
  public logout(
    { patchState, getState }: StateContext<UserModel>,
    _action: LOGOUT,
  ) {
    this.markLogoutPending(patchState);
    // Scrub the persisted session on any logout outcome (the user intends to end
    // the session). clearSession() no longer removes it, so do it explicitly
    // here rather than only on the success path.
    this.authSessionFacade.removePersistedSession();

    if (!getState().user?.userId) {
      this.clearClientSession(patchState);
      this.navigateToLogin();
      return of(null);
    }

    return this.authSessionFacade.logoutSession().pipe(
      tap(() => {
        this.clearClientSession(patchState);
        this.crossAppNavigationService.redirectAfterSignOut();
      }),
      catchError(() => {
        this.clearClientSession(patchState);
        this.navigateToLogin();
        return of(null);
      }),
    );
  }

  @Action(NAVIGATE_LOGIN_PAGE)
  public navigateLoginPage({ patchState }: StateContext<UserModel>) {
    this.clearClientSession(patchState);
    this.navigateToLogin();
  }

  private getUserRole(state) {
    if (state) {
      if (state.admin) {
        return USERROLE.ADMIN;
      }

      if (state.agent) {
        return USERROLE.AGENT;
      }

      if (state.branchAdmin) {
        return USERROLE.BRANCH;
      }

      if (state.callCentreOrBancaAgent) {
        return USERROLE.BANCA;
      }
    }
    return null;
  }

  @Action(UPDATE_PREFER_NAME)
  public updatePreferName(
    { patchState, getState }: StateContext<UserModel>,
    { payload }: UPDATE_PREFER_NAME,
  ) {
    const state = getState();
    patchState({
      user: {
        ...state.user,
        preferName: payload.preferName,
      },
    });
  }

  @Action(SET_IS_PASSWORD_EXPIRING_IND)
  public setIsPasswordExpiringInd(
    { patchState }: StateContext<UserModel>,
    { payload }: SET_IS_PASSWORD_EXPIRING_IND,
  ) {
    patchState({
      isPasswordExpiring: payload,
    });
  }

  @Action(SET_IS_PASSWORD_EXPIRED_IND)
  public setIsPasswordExpiredInd(
    { patchState }: StateContext<UserModel>,
    { payload }: SET_IS_PASSWORD_EXPIRED_IND,
  ) {
    patchState({
      isPasswordExpired: payload,
    });
  }

  @Action(STORE_QUERY_PARAMS)
  public storeQueryParamsContractId(
    { patchState },
    { payload }: STORE_QUERY_PARAMS,
  ): StateContext<UserModel> {
    return patchState({
      queryParams: payload,
    });
  }

  @Action(RESTORE_AUTH_SESSION)
  public restoreAuthSession(
    { patchState, getState }: StateContext<UserModel>,
    { payload }: RESTORE_AUTH_SESSION,
  ) {
    if (!this.authSessionFacade.restorePersistedSession()) {
      this.clearClientSession(patchState);
      return;
    }

    const user = this.authSessionFacade.userSnapshot;
    if (!user?.userId) {
      this.clearClientSession(patchState);
      return;
    }

    const queryParams =
      payload?.queryParams ?? this.authSessionFacade.queryParamsSnapshot;
    const dashboardPermissionFlags = resolveDashboardPermissionFlags(user);

    patchState({
      ...getState(),
      isLoggedIn: true,
      user,
      role: this.getUserRole(user),
      ...dashboardPermissionFlags,
      queryParams,
      error: null,
      errorDesc: null,
      loading: false,
    });

    this.authSessionFacade.setSession(user, queryParams ?? {});
  }

  @Action(GET_SASC_EMAIL_CONFIG)
  public getSascEmailConfig(
    { patchState }: StateContext<UserModel>,
    { payload }: GET_SASC_EMAIL_CONFIG,
  ) {
    return this.userService.getSascEmailConfig(payload).pipe(
      map((res) => {
        if (res?.data) {
          patchState({
            emailConfig: {
              ...res.data,
              noRecordFound: false,
            },
          });
          return;
        }

        patchState({
          emailConfig: {
            userId: payload,
            emailNotificationInd: false,
            loopNotificationInd: false,
            noRecordFound: true,
          },
        });
      }),
      catchError(() => {
        patchState({
          emailConfig: {
            userId: payload,
            emailNotificationInd: false,
            loopNotificationInd: false,
            noRecordFound: true,
          },
        });
        return EMPTY;
      }),
    );
  }

  @Action(SAVE_SASC_EMAIL_CONFIG)
  public saveSascEmailConfig(
    _stateContext: StateContext<UserModel>,
    { payload }: SAVE_SASC_EMAIL_CONFIG,
  ) {
    return this.userService.saveSascEmailConfig(payload).pipe(
      catchError(() => {
        return EMPTY;
      }),
    );
  }

  // Authentication actions
  @Action(LOGIN)
  public login(
    { patchState, getState }: StateContext<UserModel>,
    { payload }: LOGIN,
  ) {
    const state = getState();
    return this.userService.login(payload).pipe(
      map((res) => {
        const normalized = normalizeLoginResponse(res);

        if (!normalized.isLoggedInSuccess) {
          if (normalized.isUserSessionActive) {
            patchState({
              isUpdatePassword: false,
              isUserSessionActive: true,
              error: normalized.error ?? normalized.errorCode,
              errorDesc: normalized.message,
            });
          } else {
            patchState({
              isUpdatePassword: false,
              isUserSessionActive: false,
              error: normalized.error ?? normalized.errorCode,
              errorDesc: normalized.message,
            });
          }
        } else {
          const userDetail = normalized.userDetail ?? {};
          const dashboardPermissionFlags =
            resolveDashboardPermissionFlags(userDetail);
          this.authSessionFacade.setSession(
            userDetail,
            state.queryParams ?? {},
          );

          if (!normalized.isUpdatePassword) {
            patchState({
              isLoggedIn: true,
              isUserSessionActive: false,
              user: userDetail,
              error: null,
              errorDesc: null,
              tempPass: null,
              tempUserId: null,
              ...dashboardPermissionFlags,
              lastLoginDate: normalized.lastLoginDate,
              isUpdatePassword: false,
              isPasswordExpiring: normalized.isPasswordExpiring,
              isPasswordExpired: normalized.isPasswordExpired,
              passwordExpiringInDays: normalized.passwordExpiringInDays,
              firstTimeLoginState: {
                isFirstTimeLoginSuccess: false,
                firstTimeLoginErrorMsg: null,
                loginChgPswdInd: false,
              },
            });
          } else {
            patchState({
              user: userDetail,
              ...dashboardPermissionFlags,
              isUpdatePassword: true,
              isUserSessionActive: false,
              error: null,
              errorDesc: null,
              tempPass: null,
              tempUserId: null,
              lastLoginDate: normalized.lastLoginDate,
              isPasswordExpiring: normalized.isPasswordExpiring,
              isPasswordExpired: normalized.isPasswordExpired,
              passwordExpiringInDays: normalized.passwordExpiringInDays,
              firstTimeLoginState: {
                isFirstTimeLoginSuccess: false,
                firstTimeLoginErrorMsg: null,
                loginChgPswdInd: !!userDetail["loginChgPswdInd"],
              },
            });
          }
        }
      }),
    );
  }

  @Action(RESET_LOGIN_RESPONSE)
  public resetLoginResponse({ patchState }: StateContext<UserModel>) {
    patchState({
      secretQuestion: null,
      error: null,
      canViewApprovalRadioButton: false,
      canViewApprovalTab: false,
      canViewMTApprovalTab: false,
      isUserSessionActive: false,
      invalidUser: false,
      secretQuestionError: null,
      isUpdatePassword: false,
      secretAnswerWrongPassword: false,
      firstTimeLoginState: {
        isFirstTimeLoginSuccess: false,
        firstTimeLoginErrorMsg: null,
      },
    });
  }

  @Action(SAVE_USER_VALIDATION)
  public saveUserValidation(
    { patchState }: StateContext<UserModel>,
    { payload }: SAVE_USER_VALIDATION,
  ) {
    patchState({
      tempUserId: payload.tempUserId,
      tempPass: payload.tempPass,
    });
  }

  @Action(SUBMIT_FIRST_TIME_LOGIN)
  public submitFirstTimeLogin(
    { patchState, getState }: StateContext<UserModel>,
    { payload }: SUBMIT_FIRST_TIME_LOGIN,
  ) {
    return this.userService.submitFirstTimeLogin(payload).pipe(
      map((res) => {
        const state = getState();
        const isSuccess = !!res?.success;
        const mergedUser = state.user
          ? {
              ...state.user,
              preferName: payload.preferName ?? state.user.preferName,
              email: payload.email ?? state.user.email,
              mobileNo: payload.mobileNo ?? state.user.mobileNo,
              secretQuestion:
                payload.secretQuestion ?? state.user.secretQuestion,
            }
          : state.user;

        patchState({
          user: mergedUser,
          firstTimeLoginState: {
            isFirstTimeLoginSuccess: isSuccess,
            firstTimeLoginErrorMsg: isSuccess
              ? null
              : (res?.error ??
                res?.message ??
                "Unable to update your profile."),
          },
          tempUserId: isSuccess ? payload.userId : state.tempUserId,
          tempPass: isSuccess ? payload.newPassword : state.tempPass,
          isUpdatePassword: !isSuccess,
          isPasswordExpiring: isSuccess ? false : state.isPasswordExpiring,
          isPasswordExpired: false,
        });
      }),
      catchError((err: Error) => {
        patchState({
          firstTimeLoginState: {
            isFirstTimeLoginSuccess: false,
            firstTimeLoginErrorMsg: err?.message ?? null,
          },
        });
        return EMPTY;
      }),
    );
  }

  @Action(SET_LOGGED_IN_STATE)
  public setLoggedInState({ patchState }: StateContext<UserModel>) {
    patchState({
      isLoggedIn: true,
    });
  }

  @Action(GET_SECRET_QUESTION)
  public getSecretQuestionByUserId(
    { patchState }: StateContext<UserModel>,
    { payload }: GET_SECRET_QUESTION,
  ) {
    patchState({ tempUserId: payload });
    return this.userService.getsecretQuestion(payload).pipe(
      map((res: SecretQuestionResponse) => {
        if (res) {
          if (res.error) {
            patchState({
              searchUserResetPasswordErrorTitle: res
                ? res.error
                : "User ID not found",
              searchUserResetErrorMsg: res ? res.message : "User ID not found",
              secretQuestion: null,
              secretQuestionNo: null,
            });
          } else {
            const secretQuestionDescription =
              res.secretQuestionDescription ?? res.secretQuestionDescp;
            const secretQuestionCode =
              res.secretQuestionCode ?? res.secretQuestion;

            patchState({
              secretQuestion: secretQuestionDescription,
              secretQuestionNo: secretQuestionCode,
              searchUserResetPasswordErrorTitle: null,
              searchUserResetErrorMsg: null,
            });
          }
        } else {
          patchState({
            searchUserResetErrorMsg: "User ID not found",
            searchUserResetPasswordErrorTitle: null,
            secretQuestion: null,
            secretQuestionNo: null,
          });
        }
      }),
    );
  }

  @Action(SUBMIT_VALIDATION_QUESTION)
  public submitValidationQuestion(
    { patchState, getState }: StateContext<UserModel>,
    { payload }: SUBMIT_VALIDATION_QUESTION,
  ) {
    patchState({ error: null, errorDesc: null });
    return this.userService.submitValidationQuestion(payload).pipe(
      map((res) => {
        const normalized = normalizeLoginResponse(res);

        if (!normalized.isLoggedInSuccess) {
          if (normalized.message) {
            patchState({
              secretQuestionError:
                normalized.error ?? normalized.errorCode ?? normalized.message,
              secretAnswerWrongPassword: true,
              isUserSessionActive: false,
            });
          } else {
            patchState({
              secretQuestionError: normalized.error ?? normalized.errorCode,
              secretAnswerWrongPassword: false,
            });
          }
        } else {
          const state = getState();
          const userDetail = normalized.userDetail ?? {};
          const dashboardPermissionFlags =
            resolveDashboardPermissionFlags(userDetail);

          this.authSessionFacade.setSession(
            userDetail,
            state.queryParams ?? {},
          );

          patchState({
            isLoggedIn: true,
            isUpdatePassword: normalized.isUpdatePassword,
            isPasswordExpiring: normalized.isPasswordExpiring,
            isPasswordExpired: normalized.isPasswordExpired,
            passwordExpiringInDays: normalized.passwordExpiringInDays,
            secretQuestionError: null,
            user: userDetail,
            secretQuestion: null,
            secretQuestionNo: null,
            isUserSessionActive: false,
            role: this.getUserRole(userDetail),
            secretAnswerWrongPassword: false,
            lastLoginDate: normalized.lastLoginDate,
            error: null,
            errorDesc: null,
            tempPass: null,
            tempUserId: null,
            ...dashboardPermissionFlags,
          });
        }
      }),
    );
  }

  private markLogoutPending(
    patchState: StateContext<UserModel>["patchState"],
  ): void {
    patchState({
      isLoggedIn: false,
      isUserSessionActive: false,
      loading: false,
      error: null,
      errorDesc: null,
    });
  }

  private clearClientSession(
    patchState: StateContext<UserModel>["patchState"],
  ): void {
    this.authSessionFacade.clearSession();
    patchState({
      user: null,
      loading: false,
      role: USERROLE.ADMIN,
      emailConfig: null,
      canViewApprovalRadioButton: false,
      canViewApprovalTab: false,
      canViewMTApprovalTab: false,
      queryParams: {},
      menu: null,
      isLoggedIn: false,
      firstTimeLoginState: null,
      secretQuestion: null,
      secretQuestionNo: null,
      isUserSessionActive: false,
      lastLoginDate: null,
      error: null,
      errorDesc: null,
      invalidUser: false,
      tempUserId: null,
      tempPass: null,
      updatePassword: null,
      disableLoading: false,
      secretQuestionError: null,
      isUpdatePassword: false,
      secretAnswerWrongPassword: false,
      isAdministratorActivated: false,
      searchUserResetPasswordErrorTitle: null,
      searchUserResetErrorMsg: null,
      clubMember: null,
      isPasswordExpiring: false,
      isPasswordExpired: false,
      passwordExpiringInDays: null,
    });
  }

  private navigateToLogin(): void {
    localStorage.removeItem("loginSource");
    this.ngZone.run(() => {
      this.router.navigate(["/login"]);
    });
  }
}
